/**
 * Chat binding: map the broker's payload onto an outgoing chat context.
 *
 * This is the only place that knows the wire key `onScreenView`, that chat
 * has "screen chips", or that delivery is conditional on one matching by
 * entity id. The core emits `{ entityId, data }` and stops there.
 */
import { noteDelivery } from '../lib/core/diagnostics';
import { getViewSurfaceScreenData } from '../lib/core/snapshot';

/** Minimal structural shape: any already-wire-shaped screen context. */
export interface ScreenChipLike {
  type?: unknown;
  entityId?: unknown;
  entityData?: Record<string, unknown>;
}

/** Wire key the payload rides under. Chat-specific by definition. */
export const ON_SCREEN_VIEW_KEY = 'onScreenView';

/**
 * Merge the active surface's payload into the ONE screen chip it belongs to,
 * matched by entity id. Other contexts pass through untouched.
 *
 * If the user dismissed the matching chip there is nothing to attach to, and
 * nothing is sent — that is the correct consent semantic, not a bug.
 *
 * Reports delivery to the broker's diagnostics either way: building a payload
 * and delivering one are separate events, and only the second is evidence.
 */
export function mergeViewSurfaceIntoContexts<T extends ScreenChipLike>(
  contexts: readonly T[]
): T[] {
  const supplied = getViewSurfaceScreenData();
  const candidates = contexts
    .filter((c) => c.type === 'screen')
    .map((c) => (typeof c.entityId === 'string' ? c.entityId : '<none>'));

  if (!supplied) {
    return [...contexts];
  }

  let delivered = false;
  const merged = contexts.map((c) => {
    if (c.type !== 'screen' || c.entityId !== supplied.entityId) {
      return c;
    }
    delivered = true;
    return {
      ...c,
      entityData: {
        ...c.entityData,
        [ON_SCREEN_VIEW_KEY]: supplied.data,
      },
    };
  });

  noteDelivery(delivered, supplied.entityId, candidates);
  return merged;
}
